/**
 * Register export (`export-advanced`) failures -> the one line the Export dialog shows (AVA-602).
 *
 * The export request uses `responseType: 'blob'`, so an error body arrives as a Blob. It is read
 * back to text first, then classified exactly like a table-data read (`classifyTableLoadError`):
 * typed 400 REGISTER_ASSIGNMENT_MODE_CONFIGURATION_REQUIRED, 403, anything else. Only fixed copy
 * is returned - the server's own message text is never shown.
 *
 * No Vue / Vuetify / `@/` imports: unit-tested with `node --test`.
 */
import { REGISTER_NOT_CONFIGURED_COPY } from '../../../utils/registerConfigurationError.js';
import { TABLE_LOAD_STATE, classifyTableLoadError } from './registerTableStates.js';

export const REGISTER_EXPORT_ERROR_COPY = Object.freeze({
  configurationRequired: REGISTER_NOT_CONFIGURED_COPY.message,
  forbidden: "You don't have permission to export this register.",
  failed: 'Failed to export data. Please try again.',
});

function isBlobLike(data) {
  return !!data && typeof data === 'object' && typeof data.text === 'function';
}

/** The error with a Blob response body replaced by its text; anything else is returned as is. */
async function withReadableBody(err) {
  const response = err && typeof err === 'object' ? err.response : null;
  if (!response || !isBlobLike(response.data)) return err;
  let text = '';
  try {
    text = await response.data.text();
  } catch (_readErr) {
    text = '';
  }
  return { ...err, response: { ...response, data: text } };
}

/**
 * Fixed, user-facing message for a failed register export.
 * @param {unknown} err axios error (or anything the export promise rejected with)
 * @returns {Promise<string>}
 */
export async function registerExportErrorMessage(err) {
  const { state } = classifyTableLoadError(await withReadableBody(err));
  if (state === TABLE_LOAD_STATE.CONFIGURATION_REQUIRED) return REGISTER_EXPORT_ERROR_COPY.configurationRequired;
  if (state === TABLE_LOAD_STATE.FORBIDDEN) return REGISTER_EXPORT_ERROR_COPY.forbidden;
  return REGISTER_EXPORT_ERROR_COPY.failed;
}
